import CellValue from '@app/shared/components/cell-value/CellValue';
import { l } from '@locale/setupI18n';
import { FC } from 'react';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters/number-value-formatter';
import {
  getWaterfallCaseYearInstrumentTrancheValue,
  getWaterfallCaseYearInstrumentValue,
  getWaterfallCaseYearInstrumentFirstTrancheValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import {
  amountRules,
  enteredPercentageViewRules,
  decimalCalculatedViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { PayoutLogicDto } from '@app/shared/models/contracts/project-dto';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import { getPvLabel, ValuationSection } from './utils/valuationLabelHelper';

export const InstrumentExitProceeds: FC<{
  shouldDisplayInterimCashflow: boolean;
  tranche: PayoutLogicDto | undefined;
  instrument: InstrumentDefinitionWithId;
  trancheIndex: number | undefined;
  columnsCount: number;
  hasMultipleTranches: boolean;
  interpolationOption: {
    escapeValue: boolean;
  };
}> = ({
  shouldDisplayInterimCashflow,
  tranche,
  instrument,
  trancheIndex,
  columnsCount,
  hasMultipleTranches,
  interpolationOption,
}) => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const maxDataColspanCount = columnsCount - 1;
  return (
    <>
      {shouldDisplayInterimCashflow && (
        <tr>
          <th className="table-primary__cell--header-tertiary">
            {instrument.payoutLogic && instrument.payoutLogic.length > 1 && trancheIndex
              ? `${l('_ExitProceeds')} - ${l('_TrancheN', { n: trancheIndex })}`
              : l('_ExitProceeds')}
          </th>
          <th className="table-primary__cell--header-tertiary" colSpan={maxDataColspanCount} />
        </tr>
      )}
      <TableSectionValuesPerForecast
        title={l('_WeightedProceeds')}
        additionalTitleClassNames="table-primary__cell--strong"
        renderSpacer={false}
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...amountRules}
            className="table-primary__cell--strong"
            value={
              hasMultipleTranches
                ? getWaterfallCaseYearInstrumentTrancheValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    instrument.instrumentId,
                    tranche!.id,
                    (tranche) => tranche.weightedTotalDistributions
                  )
                : getWaterfallCaseYearInstrumentValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    instrument.instrumentId,
                    (instrument) => instrument.weightedTotalDistributions
                  )
            }
            dataTestId={`weighted-proceeds-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerForecast
        title={l('_PostIPODLOM')}
        renderSpacer={false}
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...enteredPercentageViewRules}
            value={getCalculatedProjectValueInPercent(
              hasMultipleTranches
                ? Number(
                    getWaterfallCaseYearInstrumentTrancheValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      instrument.instrumentId,
                      tranche!.id,
                      (tranche) => tranche.weightedPostIpoSaleRestrictionDlom
                    )
                  )
                : Number(
                    getWaterfallCaseYearInstrumentValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      instrument.instrumentId,
                      (instrument) => instrument.weightedPostIpoSaleRestrictionDlom
                    )
                  )
            )}
            dataTestId={`post-ipo-dlom-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerForecast
        title={l('_NetExitWeightedProceeds')}
        additionalTitleClassNames="table-primary__cell--strong"
        renderSpacer={false}
        highlight
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...amountRules}
            className="table-primary__cell--strong"
            value={
              hasMultipleTranches
                ? getWaterfallCaseYearInstrumentTrancheValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    instrument.instrumentId,
                    tranche!.id,
                    (tranche) => tranche.netDistributions
                  )
                : getWaterfallCaseYearInstrumentValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    instrument.instrumentId,
                    (instrument) => instrument.netDistributions
                  )
            }
            dataTestId={`net-exit-weighted-proceeds-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerForecast
        title={l('_CostOfEquity')}
        renderSpacer={false}
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...enteredPercentageViewRules}
            value={
              hasMultipleTranches
                ? getCalculatedProjectValueInPercent(
                    getWaterfallCaseYearInstrumentTrancheValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      instrument.instrumentId,
                      tranche!.id,
                      (tranche) => tranche.cumulativeCostOfEquityPc
                    )
                  )
                : // cumulativeCostOfEquityPc property is not present in CaseYearInstrument,
                  // and so it needs to be obtained from the generated tranche in the results
                  getCalculatedProjectValueInPercent(
                    getWaterfallCaseYearInstrumentFirstTrancheValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      instrument.instrumentId,
                      (tranche) => tranche.cumulativeCostOfEquityPc
                    )
                  )
            }
            dataTestId={`cost-of-equity-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerForecast
        title={l('_DiscountFactor')}
        renderSpacer={false}
        renderCell={({ forecast, caseItem }) => {
          return (
            <CellValue
              {...decimalCalculatedViewRules}
              value={
                hasMultipleTranches
                  ? getWaterfallCaseYearInstrumentTrancheValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      instrument.instrumentId,
                      tranche!.id,
                      (tranche) => tranche.cumulativeDiscountFactor
                    )
                  : // cumulativeDiscountFactor property is not present in CaseYearInstrument,
                    // and so it needs to be obtained from the generated tranche in the results
                    getWaterfallCaseYearInstrumentFirstTrancheValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      instrument.instrumentId,
                      (tranche) => tranche.cumulativeDiscountFactor
                    )
              }
              dataTestId={`discount-factor-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          );
        }}
      />
      <TableSectionValuesPerForecast
        title={getPvLabel({
          section: ValuationSection.ExitProceeds,
          instrument,
          trancheIndex,
          hasInterims: shouldDisplayInterimCashflow,
          interpolationOption,
        })}
        additionalTitleClassNames="table-primary__cell--strong"
        renderSpacer
        highlight={hasMultipleTranches}
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...amountRules}
            className="table-primary__cell--strong"
            value={
              hasMultipleTranches
                ? getWaterfallCaseYearInstrumentTrancheValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    instrument.instrumentId,
                    tranche!.id,
                    (tranche) => tranche.fairValue
                  )
                : getWaterfallCaseYearInstrumentValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    instrument.instrumentId,
                    (instrument) => instrument.fairValue
                  )
            }
            dataTestId={`pv-of-instrument-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
    </>
  );
};
